import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  useDraggable,
  useDroppable,
  PointerSensor,
  KeyboardSensor,
  KeyboardCode,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  filterOwnerOnly,
  ownerFilterAvailable,
  backlogFilterPills,
  filterBacklogRows,
  initialsName,
  formatDay,
  formatDayShort,
  resolvePlanSelection,
  defaultPlanSelection,
  planFocusSelection,
  planTileDragId,
  planTileKey,
  backlogRowDragId,
  pinnedRowDragId,
  importProvenanceLabel,
  mondayOfDate,
  IMPORT_FILE_ACCEPT,
  laneAcceptsProject,
} from "../api/shape.js";
import { forecastDragId, FORECAST_LABEL } from "../api/planForecast.js";
import RecordReview from "./day/RecordReview.jsx";
import RecordSheet from "./day/RecordSheet.jsx";
import { usePhone } from "../phone/usePhone.js";
import PhonePlan from "../phone/PhonePlan.jsx";
import {
  SCHEDULE_CATEGORIES,
  SCHEDULE_INSPECTION_TYPES,
  defaultScheduleForm,
  rosterForProject,
  validateScheduleForm,
} from "../api/speed.js";
const DOT_GRIP = (
  <svg width="12" height="14" viewBox="0 0 12 14" fill="#64748b" style={{ flexShrink: 0 }} aria-hidden="true">
    <circle cx="3" cy="2" r="1.3"></circle>
    <circle cx="9" cy="2" r="1.3"></circle>
    <circle cx="3" cy="7" r="1.3"></circle>
    <circle cx="9" cy="7" r="1.3"></circle>
    <circle cx="3" cy="12" r="1.3"></circle>
    <circle cx="9" cy="12" r="1.3"></circle>
  </svg>
);

/**
 * Keyboard coordinateGetter for the Plan grid's free-form droppable cells
 * (not a sortable list, so @dnd-kit/sortable's own coordinateGetter does not
 * apply). Arrow keys step to the next droppable rect in that screen
 * direction; dnd-kit's collision detection (`closestCenter`) then resolves
 * which cell the move actually lands on.
 */
function gridCoordinateGetter(event, { context }) {
  const { active, droppableRects, droppableContainers } = context;
  if (!active) return undefined;
  const current = droppableRects.get(active.id) || (active.rect && active.rect.current && active.rect.current.translated);
  const cx = current ? current.left + current.width / 2 : 0;
  const cy = current ? current.top + current.height / 2 : 0;
  const dirs = {
    ArrowRight: [1, 0],
    ArrowLeft: [-1, 0],
    ArrowDown: [0, 1],
    ArrowUp: [0, -1],
  };
  const dir = dirs[event.key];
  if (!dir) return undefined;
  event.preventDefault();
  let best = null;
  let bestScore = Infinity;
  for (const container of droppableContainers.values()) {
    const rect = droppableRects.get(container.id);
    if (!rect) continue;
    const tx = rect.left + rect.width / 2;
    const ty = rect.top + rect.height / 2;
    const dx = tx - cx;
    const dy = ty - cy;
    const along = dx * dir[0] + dy * dir[1];
    if (along <= 0) continue; // must be strictly in the requested direction
    const across = Math.abs(dx * dir[1] - dy * dir[0]);
    const score = along + across * 4;
    if (score < bestScore) {
      bestScore = score;
      best = { x: tx, y: ty };
    }
  }
  return best || undefined;
}

/**
 * The grid's own column template (owner review 2026-10, round 2 — the
 * canvas's `grid-template-columns: 120px repeat(7, minmax(130px, 1fr))`).
 * Shared literally between the day-header row and every `LaneRow` so their
 * columns line up pixel for pixel; a day column never shrinks below 150px,
 * which is what makes a two-line tile readable — the grid itself may run
 * wider than its scroll container, which the `.list` wrapper (overflow-x:
 * auto) scrolls to reveal rather than squeezing the columns to fit.
 */
const GRID_TEMPLATE_COLUMNS = "120px repeat(7, minmax(130px, 1fr))";
const GRID_MIN_WIDTH = 1030;

/**
 * Grid tile. Canvas layout: line 1 time + project code on the left, the
 * chip on the right, same row; line 2 the title across the tile's full
 * width, up to 2 lines (line-clamp), ellipsis only past that.
 *
 * The WHOLE tile is the drag source and the selection control (owner/codex
 * review 2026-10, live-journey run 37666661909 — the previous round put
 * dnd-kit's pointer listeners on a tiny ⋮⋮ handle only, which broke the
 * live journey's own `dragTileToCell` (it drags from the tile's centre, the
 * way a person does) and would have confused the owner, who also drags by
 * the body, not a 12px handle). `useDraggable`'s PointerSensor (4px
 * activation distance, configured in the parent `DndContext`) already
 * distinguishes a plain click (no movement) from a drag (movement past the
 * threshold) — attaching its `onPointerDown` to the outer tile, alongside a
 * normal `onClick`, is enough: a click without movement still fires
 * `onClick` normally (dnd-kit never calls `preventDefault` until a drag
 * actually starts), and a move past 4px starts the drag instead.
 *
 * Keyboard: the tile itself is both dnd-kit's keyboard-activation node and
 * the selection control (codex review finding, #1978 — `{...attributes}`
 * on the ⋮⋮ handle made IT a separate focusable `role="button"` nested
 * inside the tile's own button, and a keydown on it bubbles to the tile's
 * own handler too, double-firing). dnd-kit requires its keyboard activator
 * to be the SAME node `onPointerDown`/`ref` are on — `KeyboardSensor`
 * checks `event.target === active.activatorNode.current`, which defaults
 * to the main draggable node, never a child (confirmed against
 * @dnd-kit/core's own source) — so the handle carries NO dnd attributes,
 * no listeners and no tabIndex at all now (`aria-hidden`, purely visual).
 * Enter and Space never collide because they are configured to mean
 * different things (`keyboardCodes` on the `KeyboardSensor`, set up where
 * `sensors` is defined, below): Space starts/ends a keyboard drag, Enter
 * always selects — `dnd-kit`'s own activator handler no-ops for any code
 * outside its configured `start` list, so composing both checks on one
 * `onKeyDown` is safe.
 */
/**
 * Owner retest 4: which import each schedule row came from. `runsById` maps
 * runId -> { fileName, importedAt, firstPlannedDate } (import history);
 * `filterRunId` is the active "From import" filter (null = off).
 */
const ImportProvenanceContext = createContext({ runsById: {}, filterRunId: null });

function Tile({ tile, pinned, saving, selected, onSelect }) {
  const { runsById, filterRunId } = useContext(ImportProvenanceContext);
  const fromImport = Boolean(filterRunId) && tile.sourceRunId === filterRunId;
  const dimmed = Boolean(filterRunId) && !fromImport;
  const provenance = importProvenanceLabel(tile.sourceRunId, tile.importedAtUtc, runsById);
  // Project-qualified drag id (codex review finding, PR this comment ships
  // with): `scheduleRef` alone can collide across two different projects'
  // own lists — dnd-kit requires every id in the DndContext to be globally
  // unique, and two tiles sharing a bare scheduleRef would otherwise be
  // indistinguishable to it (and to the `selected` key comparison below).
  const id = pinned ? `pinned:${planTileKey(tile)}` : planTileDragId(tile);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: { tile, pinned: Boolean(pinned) },
  });
  const style = {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    borderRadius: 6,
    padding: "6px 8px",
    minHeight: 44,
    minWidth: 0,
    opacity: isDragging ? 0.4 : dimmed ? 0.35 : 1,
    outline: selected ? "1px solid var(--acc)" : fromImport ? "1px solid var(--acc-br)" : undefined,
    background: fromImport ? "var(--acc-bg)" : "var(--raised)",
    cursor: onSelect ? "pointer" : undefined,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };
  const chip = saving ? (
    <div className="chip mute" data-testid="plan-tile-saving" style={{ flexShrink: 0 }}>
      Saving…
    </div>
  ) : tile.chipText ? (
    <div className={`chip ${tile.chipClass}`} style={{ flexShrink: 0 }}>{tile.chipText}</div>
  ) : null;
  // Space -> dnd-kit's own keyboard-drag activator (no-ops for any other
  // code, since the sensor is configured with keyboardCodes.start:
  // [Space] below); Enter -> select. Composed on one handler because both
  // must live on the SAME node dnd-kit's keyboard activation checks against
  // (see the component doc comment above).
  function handleKeyDown(e) {
    listeners.onKeyDown(e);
    if (onSelect && e.key === "Enter") {
      e.preventDefault();
      onSelect(tile);
    }
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid="plan-tile"
      data-schedule-ref={tile.scheduleRef}
      data-from-import={fromImport ? "true" : undefined}
      {...attributes}
      onPointerDown={listeners.onPointerDown}
      onClick={onSelect ? () => onSelect(tile) : undefined}
      onKeyDown={handleKeyDown}
    >
      {/* Visual cue only — no dnd attributes, no listeners, not focusable.
          Dragging (pointer or keyboard) and selecting both live on the
          tile itself, above. */}
      <span aria-hidden="true" style={{ display: "flex", flexShrink: 0, marginTop: 2 }}>
        {DOT_GRIP}
      </span>
      <div data-testid="plan-tile-body" style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Line 1: time + project code on the left, chip on the right —
            never competing with the title's own width (line 2, below). */}
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 4, alignItems: "baseline", minWidth: 0, overflow: "hidden" }}>
            {tile.plannedTime ? (
              <div className="mono" style={{ fontSize: 10.5, fontWeight: 600, color: "var(--t1)", flexShrink: 0, whiteSpace: "nowrap" }}>
                {tile.plannedTime}
              </div>
            ) : null}
            <div className="mono" style={{ fontSize: 10, color: "var(--t3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
              {tile.code}
            </div>
          </div>
          {chip}
        </div>
        {/* Line 2: the title across the tile's full width — up to 2 lines,
            ellipsis only once it overflows a second line (owner review
            2026-10: a day column now wide enough, per GRID_TEMPLATE_COLUMNS
            above, for a title like "Ballast tank final, No.5 WBT" to read
            in full or wrap once, never truncate to a handful of letters). */}
        <div
          style={{
            fontSize: "11.5px",
            color: "var(--t1)",
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          {tile.title}
        </div>
        {provenance ? (
          <div data-testid="plan-tile-import" title={provenance} style={{ fontSize: 10, color: "var(--t3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
            {provenance}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DayCell({ laneKey, date, tiles, savingRef, selectedKey, onSelectTile, accepts = true, rosterCodes = [] }) {
  // A lane whose inspector is not on the dragged item's project roster is not
  // a drop target: the backend always refuses it (inspector_not_on_roster).
  // It stays registered with dnd-kit on purpose (a disabled droppable is
  // skipped by collision detection, so a drop on a dimmed lane would snap to
  // the nearest VALID cell and move the item somewhere nobody pointed at);
  // handleDragEnd ignores the drop instead, so it does nothing.
  const { setNodeRef, isOver } = useDroppable({ id: `cell:${laneKey}|${date}`, data: { upn: laneKey, date } });
  return (
    <div
      ref={setNodeRef}
      data-testid="plan-day-cell"
      data-lane-key={laneKey}
      data-date={date}
      data-droppable={accepts ? "true" : "false"}
      data-roster-codes={rosterCodes.join(" ")}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 6,
        minHeight: 56,
        minWidth: 0,
        borderLeft: "1px solid var(--line)",
        background: isOver && accepts ? "var(--acc-bg)" : undefined,
        opacity: accepts ? undefined : 0.4,
      }}
    >
      {tiles.map((tile) => (
        <Tile
          key={tile.key}
          tile={tile}
          saving={Boolean(savingRef) && tile.scheduleRef === savingRef}
          selected={Boolean(selectedKey) && planTileKey(tile) === selectedKey}
          onSelect={onSelectTile}
        />
      ))}
    </div>
  );
}

function LaneRow({ lane, days, savingRef, selectedKey, onSelectTile, dragCode = null }) {
  const accepts = laneAcceptsProject(lane, dragCode);
  return (
    <div data-testid="plan-lane" data-lane-key={lane.key} data-lane-accepts={accepts ? "true" : "false"} style={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE_COLUMNS, minWidth: GRID_MIN_WIDTH, borderTop: "1px solid var(--line)" }}>
      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <div style={{ fontSize: "12.5px", fontWeight: 600, color: "var(--t1)", opacity: accepts ? undefined : 0.5 }}>{lane.name}</div>
        {/* No inspector discipline/role field exists on the backend roster
            (azure-functions/cis/shared/roster.js carries only sm:/upn) —
            never invented — but this empty line keeps the lane row's height
            matching the artboard's three-line label rhythm rather than
            collapsing shorter than every day cell's tiles expect. */}
        <div style={{ fontSize: 11, lineHeight: "15px" }}>&nbsp;</div>
        <div className="mono" style={{ fontSize: 10, color: "var(--t3)" }}>
          {lane.teamLabel}
        </div>
        {accepts ? null : (
          <div data-testid="plan-lane-not-on-roster" style={{ fontSize: 10, color: "var(--warn)" }}>
            Not on {dragCode} roster
          </div>
        )}
      </div>
      {days.map((day, i) => (
        <DayCell
          key={day.date}
          laneKey={lane.key}
          date={day.date}
          tiles={lane.cells[i]}
          savingRef={savingRef}
          selectedKey={selectedKey}
          onSelectTile={onSelectTile}
          accepts={accepts}
          rosterCodes={lane.rosterCodes || lane.teams || []}
        />
      ))}
    </div>
  );
}

/**
 * One "To book" row (Plan redesign, owner-approved DEC-CHAT-20261007-412) —
 * either backlog kind (shape.js#shapeBacklogRow). An unbooked row drags the
 * same way the old due-list row did (`data-testid="plan-unbooked-row"`,
 * `data-schedule-ref`, unchanged — the live journey spec's drag step locates
 * it this way). A re-inspection row drags like Today's "Book in Plan" pinned
 * row (`pinned: true` — `onDropPinned` books a NEW schedule row carrying
 * `sourceInspectionNumber`, the exact mechanism PinnedRow below already
 * uses), under its own testid so it is never picked up by that same
 * `plan-unbooked-row` live-journey locator.
 */
/**
 * One "To book" row. Canvas layout: drag handle, title on one line with
 * ellipsis, a sub-line of "<project> · <reason/date> · <trade>", chip on
 * the right — the project code folds into the sub-line instead of its own
 * fixed column.
 *
 * The WHOLE row is the drag source and the selection control, same pattern
 * as `Tile` above and for the same reason (owner/codex review 2026-10,
 * live-journey run 37666661909): a click without movement selects, a move
 * past the PointerSensor's 4px activation distance drags. Keyboard: the
 * row itself carries dnd-kit's `attributes`/keyboard activator (required —
 * dnd-kit's `KeyboardSensor` only activates when the keydown's `target` is
 * the registered draggable node itself, never a child), with Enter and
 * Space composed on one handler meaning different things (Space starts/
 * ends a keyboard drag, Enter selects — `keyboardCodes` on the
 * `KeyboardSensor`, see `sensors` below). The ⋮⋮ handle carries no dnd
 * attributes, no listeners and no tabIndex — visual only (codex review
 * finding, #1978 — attributes on the handle made it a second, nested
 * focusable control whose keydown bubbled up to the row's own handler).
 */
function BacklogRow({ row, selected, saving, onSelect }) {
  const { filterRunId } = useContext(ImportProvenanceContext);
  const fromImport = Boolean(filterRunId) && row.sourceRunId === filterRunId;
  const dimmed = Boolean(filterRunId) && !fromImport;
  const pinned = row.kind === "reinspection";
  // Project-qualified (codex review finding) — a bare scheduleRef/
  // inspectionNumber can collide across two projects' own lists.
  const id = backlogRowDragId(row);
  // The draggable payload is the SHAPED row itself (`row`), never
  // `row.row` (codex review finding, PR this comment ships with): for an
  // unbooked row, `buildDragUpsertRow` (shape.js) reads `tile.row.*` — the
  // full schedule row shapeBacklogRow attached under `.row` — so the
  // payload must still carry that wrapper; unwrapping it here sent every
  // field as `undefined` and the booking was rejected by the server's own
  // validator. For a re-inspection row, `buildBookingRow` reads the FLAT
  // fields (`title`, `category`, `inspectionNumber`/`sourceInspectionNumber`)
  // that shapeBacklogRow already carries at the top level of `row` — the
  // same shaped object works for both kinds.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, data: { tile: row, pinned } });
  // `row.sub` is already "Our inspection <date>" for a re-inspection, or the
  // trade (`row.category`) for an unbooked row (shape.js#shapeBacklogRow) —
  // folding the project code in front never duplicates it against
  // `row.category` below, since for an unbooked row they are the same
  // string.
  const subParts = [row.code];
  if (pinned) subParts.push(row.sub);
  if (row.category && row.category !== row.sub) subParts.push(row.category);
  const sub = subParts.filter(Boolean).join(" · ");
  function handleKeyDown(e) {
    listeners.onKeyDown(e);
    if (e.key === "Enter") {
      e.preventDefault();
      onSelect(row);
    }
  }
  return (
    <div
      ref={setNodeRef}
      data-testid={pinned ? "plan-backlog-reinspection-row" : "plan-unbooked-row"}
      data-schedule-ref={pinned ? "" : row.scheduleRef || ""}
      className="r"
      data-from-import={fromImport ? "true" : undefined}
      style={{ minWidth: 0, opacity: isDragging ? 0.4 : dimmed ? 0.35 : 1, outline: selected ? "1px solid var(--acc)" : fromImport ? "1px solid var(--acc-br)" : undefined, cursor: "pointer" }}
      {...attributes}
      onPointerDown={listeners.onPointerDown}
      onClick={() => onSelect(row)}
      onKeyDown={handleKeyDown}
    >
      <span aria-hidden="true" style={{ display: "flex", flexShrink: 0 }}>
        {DOT_GRIP}
      </span>
      <div data-testid="plan-backlog-row-body" style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <div style={{ fontSize: 13, color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{row.title}</div>
        <div style={{ fontSize: 11, color: "var(--t3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{sub}</div>
      </div>
      {saving ? (
        <div className="chip mute" data-testid="plan-tile-saving" style={{ flexShrink: 0 }}>
          Saving…
        </div>
      ) : (
        <div className={`chip ${row.chipClass}`} style={{ flexShrink: 0 }}>{row.chipText}</div>
      )}
    </div>
  );
}

function PinnedRow({ pinned, saving }) {
  const id = pinnedRowDragId(pinned);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, data: { tile: pinned, pinned: true } });
  return (
    <div ref={setNodeRef} className="r" style={{ minHeight: 44, cursor: "grab", opacity: isDragging ? 0.4 : 1 }} {...listeners} {...attributes}>
      {DOT_GRIP}
      <div className="proj">{pinned.code}</div>
      <div className="id">{pinned.subId || `CI-${pinned.sourceInspectionNumber || pinned.inspectionNumber}`}</div>
      <div className="ttl">{pinned.title}</div>
      <div className={`chip ${saving ? "mute" : "acc"}`} style={{ width: "auto" }} data-testid={saving ? "plan-tile-saving" : undefined}>
        {saving ? "Saving…" : "book"}
      </div>
    </div>
  );
}

/**
 * "Yard schedule" panel (owner-approved mockup, top of the main column) —
 * carries the last-import summary line (`data-testid="plan-last-import"`,
 * kept from the old bottom-of-page row so every existing live/render test
 * anchored on it keeps working), "Import history" (SPEC-CIS-COCKPIT-
 * READINESS-ACTIONS-001 §4.5: read-only list of past imports for the one
 * selected project, 20 per page, newest first; disabled with a reason when
 * no single project is selected) and the real "Import yard schedule" control
 * (`data-testid="plan-import-button"`, unchanged behaviour).
 */
function YardSchedulePanel({ lastImport, fileInputRef, onImportButtonClick, onFileInputChange, historyEnabled, historyOpen, onToggleHistory }) {
  return (
    <div className="card" data-testid="plan-yard-schedule-panel" style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 3, flexGrow: 1, minWidth: 240 }}>
        <div className="t">Yard schedule</div>
        <div className="mono" data-testid="plan-last-import" style={{ fontSize: 11, color: "var(--t3)" }}>
          {lastImport ? lastImport.message : "No yard schedule has been imported for this project."}
        </div>
      </div>
      <button
        type="button"
        className="btn off"
        data-testid="plan-import-history-button"
        disabled={!historyEnabled}
        title={historyEnabled ? "Past yard-schedule imports" : "Select one project to see its import history"}
        onClick={onToggleHistory}
      >
        {historyOpen ? "Hide import history" : "Import history"}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={IMPORT_FILE_ACCEPT}
        data-testid="plan-import-file-input"
        style={{ display: "none" }}
        onChange={onFileInputChange}
      />
      <button type="button" className="btn acc" data-testid="plan-import-button" onClick={onImportButtonClick}>
        Import yard schedule
      </button>
    </div>
  );
}

/** Badge legend row under the week header — copy verbatim from the owner-approved mockup. */
const LEGEND = [
  { chipText: "done", chipClass: "ok", label: "result recorded" },
  { chipText: "today", chipClass: "warn", label: "booked today, no result yet" },
  { chipText: "booked", chipClass: "acc", label: "date and inspector set" },
  { chipText: "needs inspector", chipClass: "warn", label: "dated, nobody assigned" },
  { chipText: "Nd", chipClass: "bad", label: "days since our inspection" },
  { chipText: "no date", chipClass: "mute", label: "imported, not booked" },
];
/**
 * Owner retest 4: "From import: <latest import>" filter. On, rows created by
 * that import are highlighted and every other row is dimmed (nothing is
 * removed, so the week can still be read in context).
 */
function ImportFilterBar({ model, runsById, filterRunId, filterNote, onToggle, onClear }) {
  const latestId = model.lastImport && model.lastImport.runId;
  const activeId = filterRunId || latestId;
  if (!activeId) return null;
  const run = runsById[activeId];
  const name = (run && run.fileName) || (run && run.importedAt ? formatDay(run.importedAt) : null) || (model.lastImport && activeId === latestId ? formatDay(model.lastImport.importedAtUtc) : "this import");
  const inWeek = model.lanes.reduce((n, lane) => n + lane.cells.reduce((m, cell) => m + cell.filter((t) => t.sourceRunId === activeId).length, 0), 0);
  const waiting = model.backlog.filter((r) => r.sourceRunId === activeId).length;
  const on = Boolean(filterRunId);
  return (
    <div data-testid="plan-import-filter-bar" style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", fontSize: 12 }}>
      <button
        type="button"
        className={`btn ${on ? "acc" : "off"}`}
        data-testid="plan-import-filter"
        aria-pressed={on}
        onClick={() => (on ? onClear() : onToggle(activeId))}
      >
        From import: {name}
      </button>
      {on ? (
        <span data-testid="plan-import-filter-count" style={{ color: "var(--t2)" }}>
          {inWeek} on this week{waiting ? ` · ${waiting} waiting in To book` : ""}
        </span>
      ) : null}
      {on && filterNote ? <span data-testid="plan-import-filter-note" style={{ color: "var(--warn)" }}>{filterNote}</span> : null}
    </div>
  );
}

/**
 * Day redesign phase 2 (SPEC-CIS-COCKPIT-DAY-REDESIGN-P2-001 2.5, C4): the
 * yard's forward schedule. A forecast tile is a draggable item: dropped on a
 * lane cell it books the item (one upsert, `basis: ""`, the date and the
 * assignee). It is never in an inspector's lane until then, and it is labelled
 * Expected or Likely, never "requested".
 */
function ForecastTile({ item, saving, selected, onSelect }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: forecastDragId(item),
    data: { forecast: item, tile: { code: item.code, scheduleRef: item.scheduleRef } },
  });
  function handleKeyDown(e) {
    listeners.onKeyDown(e);
    // Space starts a keyboard drag (see the sensors); Enter only selects.
    if (e.key === "Enter" && onSelect) {
      e.preventDefault();
      onSelect(item);
    }
  }
  return (
    <div
      ref={setNodeRef}
      className={`plan-fc-tile ${item.confidence}`}
      data-testid="plan-forecast-tile"
      data-schedule-ref={item.scheduleRef}
      data-confidence={item.confidence}
      data-selected={selected ? "true" : undefined}
      style={{ opacity: isDragging ? 0.4 : 1, transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined, outline: selected ? "1px solid var(--acc)" : undefined }}
      {...attributes}
      onPointerDown={listeners.onPointerDown}
      onClick={onSelect ? () => onSelect(item) : undefined}
      onKeyDown={handleKeyDown}
    >
      <span>{saving ? "Saving…" : item.label}</span>
      <span className="mono">{item.kind}</span>
    </div>
  );
}

const FORECAST_SHOWN = 3;

function ForecastCell({ cell, savingRef, selectedKey, onSelectForecast }) {
  const [open, setOpen] = useState(false);
  const shown = open ? cell.items : cell.items.slice(0, FORECAST_SHOWN);
  const more = cell.items.length - shown.length;
  return (
    <div className="plan-fc-cell" data-testid="plan-forecast-cell" data-date={cell.date} data-confidence={cell.confidence || undefined}>
      <span className={`plan-fc-count ${cell.confidence}`}>{cell.count}</span>
      {cell.label ? <span className="plan-fc-label mono">{cell.label.toUpperCase()}</span> : null}
      {shown.map((item) => <ForecastTile key={item.key} item={item} saving={Boolean(savingRef) && item.scheduleRef === savingRef} selected={selectedKey === item.key} onSelect={onSelectForecast} />)}
      {more > 0 ? (
        <button type="button" className="plan-fc-more" onClick={() => setOpen(true)}>+ {more} more</button>
      ) : null}
      {cell.count === 0 ? <span className="plan-fc-none">None planned</span> : null}
    </div>
  );
}

function ForecastRow({ forecast, savingRef, selectedKey, onSelectForecast }) {
  return (
    <div data-testid="plan-forecast-row" style={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE_COLUMNS, minWidth: GRID_MIN_WIDTH, borderTop: "1px solid var(--line)", background: "var(--bg)" }}>
      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <div style={{ fontSize: "12.5px", fontWeight: 600, color: "var(--t1)" }}>Yard&apos;s forward schedule</div>
        <div style={{ fontSize: 11, color: "var(--t2)" }}>not yet requested</div>
      </div>
      {forecast.cells.map((cell) => <ForecastCell key={cell.date} cell={cell} savingRef={savingRef} selectedKey={selectedKey} onSelectForecast={onSelectForecast} />)}
    </div>
  );
}

function ForecastLegend() {
  return (
    <div data-testid="plan-forecast-legend" style={{ display: "flex", flexWrap: "wrap", gap: 16, color: "var(--t2)", fontSize: 12, alignItems: "center" }}>
      <span style={{ display: "flex", gap: 6, alignItems: "center" }}><span className="plan-fc-swatch requested" />Requested on the yard&apos;s daily list</span>
      <span style={{ display: "flex", gap: 6, alignItems: "center" }}><span className="plan-fc-swatch expected" />Expected: block schedule, next 2 days</span>
      <span style={{ display: "flex", gap: 6, alignItems: "center" }}><span className="plan-fc-swatch likely" />Likely: block schedule, may move a day or more</span>
    </div>
  );
}

function LaterWeeks({ weeks }) {
  if (!weeks.length) return null;
  return (
    <section data-testid="plan-later-weeks" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div className="sec">Later weeks &middot; from the yard&apos;s block schedule</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        {weeks.map((w) => (
          <div key={w.weekStart} data-testid="plan-later-week" className="plan-fc-week">
            <span className="mono" style={{ color: "var(--t2)" }}>{w.label}</span>
            <span style={{ fontSize: 18, fontWeight: 600, color: "var(--t1)" }}>{w.text}</span>
          </div>
        ))}
      </div>
      <p style={{ margin: 0, color: "var(--t2)", fontSize: 12 }}>The further ahead, the less exact. These are the yard&apos;s plan and move often.</p>
    </section>
  );
}

/** "Waiting for a repair check · N": opens the list, oldest first (C6). */
function RepairChecksCard({ repairChecks, initialOpen = false }) {
  const [open, setOpen] = useState(initialOpen);
  return (
    <div data-testid="plan-repair-checks" className="plan-rc-card">
      <button type="button" className="plan-rc-head" aria-expanded={open} aria-controls="plan-repair-list" data-testid="plan-repair-toggle" onClick={() => setOpen((v) => !v)}>
        <span style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontWeight: 600, color: "var(--t1)" }}>Waiting for a repair check</span>
          <span className="mono" data-testid="plan-repair-count" style={{ fontSize: 18, fontWeight: 600, color: "var(--warn)" }}>{repairChecks.count}</span>
        </span>
        <span style={{ fontSize: 12, lineHeight: 1.45, color: "var(--t2)" }}>
          We recorded defects and the yard has not asked us to see them again. {repairChecks.oldestText}
        </span>
        <span style={{ fontSize: 12, color: "var(--acc)" }}>{open ? "Hide the list" : "Show the list, oldest first"}</span>
      </button>
      {open ? (
        <ol id="plan-repair-list" data-testid="plan-repair-list" className="plan-rc-list">
          {repairChecks.items.map((i) => (
            <li key={i.key} data-testid="plan-repair-item" data-inspection={i.inspectionNumber}>
              <span style={{ color: "var(--t1)" }}>{i.code ? `${i.code} · ` : ""}{i.line}</span>
              {i.defectText ? <span style={{ color: "var(--t2)" }}>{i.defectText}</span> : null}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

function LegendRow() {
  return (
    <div data-testid="plan-legend" style={{ display: "flex", flexWrap: "wrap", gap: 16, color: "var(--t3)", fontSize: 12, alignItems: "center" }}>
      {LEGEND.map((l) => (
        <span key={l.chipText} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span className={`chip ${l.chipClass}`}>{l.chipText}</span>
          {l.label}
        </span>
      ))}
    </div>
  );
}

/**
 * The left "To book" column — `model.backlog` (shape.js#shapeBacklogRow,
 * already ranked re-inspections-overdue first, then undated rows), filter
 * pills, and the existing pinnedBooking row (Today's "Book in Plan" hand-
 * off — kept unconditionally so that flow is unaffected by this redesign).
 */
function ToBookColumn({ backlog, backlogTotal, filter, onFilterChange, canShowMore, showMoreCount, onShowMore, loadingMore, selectedKey, onSelect, savingRef, pinnedBooking }) {
  const pills = backlogFilterPills(backlog);
  const visible = filterBacklogRows(backlog, filter);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 280, maxWidth: 400 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div className="sec">To book</div>
        <div data-testid="plan-backlog-count" style={{ fontSize: 12, color: "var(--t3)" }}>
          &middot; {backlog.length} of {backlogTotal}
        </div>
      </div>
      <div data-testid="plan-backlog-filters" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {pills.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`tab ${filter === p.id ? "on" : ""}`}
            style={{ whiteSpace: "nowrap", flexShrink: 0 }}
            onClick={() => onFilterChange(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="list">
        {pinnedBooking ? (
          <PinnedRow pinned={pinnedBooking} saving={Boolean(savingRef) && (pinnedBooking.scheduleRef || pinnedBooking.inspectionNumber) === savingRef} />
        ) : null}
        {visible.length === 0 && !pinnedBooking ? (
          <div className="r">
            <div className="ttl" style={{ color: "var(--t3)" }}>
              Nothing to do
            </div>
          </div>
        ) : (
          visible.map((row) => (
            <BacklogRow
              key={row.key}
              row={row}
              selected={selectedKey === row.key}
              saving={Boolean(savingRef) && (row.scheduleRef || row.sourceInspectionNumber) === savingRef}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
      {canShowMore ? (
        <button type="button" className="btn off" data-testid="plan-backlog-show-more" disabled={loadingMore} onClick={onShowMore}>
          {/* `showMoreCount` is only a reliable count under "All projects" —
              scoped to one or more selected projects, the true number of
              further rows that will actually match is unknown until they
              are fetched and filtered (shape.js#computeBacklogPaging), so
              the label omits a number rather than guessing one. */}
          {loadingMore ? "Loading…" : showMoreCount > 0 ? `Show ${showMoreCount} more` : "Show more"}
        </button>
      ) : null}
    </div>
  );
}

/** Facts/history/primary-action for a selected "To book" row — only fields the Plan envelope actually carries (§4.3); nothing invented. */
function backlogDetail(row, runsById) {
  const facts = [];
  if (row.kind === "unbooked") {
    if (row.category) facts.push(["Trade", row.category]);
    if (row.stage) facts.push(["Stage", row.stage]);
  }
  facts.push(["Inspector", row.assignedTo ? initialsName(row.assignedTo) : "Unassigned"]);
  if (row.kind === "reinspection") {
    facts.push(["Our inspection", formatDayShort(row.inspectionDate)]);
    facts.push(["Days since", `${row.ageDays || 0}d`]);
  }
  const history = row.kind === "reinspection"
    ? [`Our inspection ${formatDayShort(row.inspectionDate)}. Re-inspection due.`]
    : [importProvenanceLabel(row.sourceRunId, row.importedAtUtc, runsById) || "Imported from the yard schedule, not yet booked."];
  return {
    title: row.title,
    status: `${row.code} · not booked${row.kind === "reinspection" ? " · re-inspection" : ""}`,
    facts,
    history,
    primaryLabel: "Book…",
    primaryKind: "book",
  };
}

/** Facts/history/primary-action for a selected grid tile — same no-invention rule as backlogDetail. */
function tileDetail(tile, runsById) {
  const row = tile.row || {};
  const facts = [];
  if (row.category) facts.push(["Trade", row.category]);
  if (row.stage) facts.push(["Stage", row.stage]);
  facts.push(["Inspector", tile.assignedTo ? initialsName(tile.assignedTo) : "Unassigned"]);
  facts.push(["Planned", formatDay(tile.plannedDate)]);
  if (tile.plannedTime) facts.push(["Time", tile.plannedTime]);
  const recorded = row.recordedInspectionNumber;
  const history = [];
  if (recorded) history.push(`Recorded as CI-${recorded}`);
  history.push(tile.assignedTo ? `Booked for ${formatDay(tile.plannedDate)} · ${initialsName(tile.assignedTo)}` : `Planned for ${formatDay(tile.plannedDate)}, no inspector yet`);
  const provenance = importProvenanceLabel(tile.sourceRunId, tile.importedAtUtc, runsById);
  if (provenance) history.push(provenance);
  const done = tile.chipClass === "ok";
  return {
    title: tile.title,
    status: `${tile.code} · ${done ? "done" : tile.chipText === "today" ? "booked today, no result yet" : tile.chipText === "needs inspector" ? "needs an inspector" : "booked"} ${formatDay(tile.plannedDate)}`,
    facts,
    history,
    // A done item with a recorded inspection opens the record sheet. A done
    // item with no recorded number has no record to open, so it has no
    // primary button at all: a button that does nothing is never drawn.
    recordNumber: done ? recorded || row.linkedInspectionNumber || 0 : 0,
    primaryLabel: done ? "Open record" : "Move…",
    primaryKind: done ? "record" : "move",
  };
}

/**
 * The right-hand detail panel. Idle (nothing selected) keeps rendering the
 * existing "Moving one" drag guidance verbatim (same text/testid-free markup
 * as before this redesign) so that explanatory copy is never lost; selecting
 * a backlog row or a grid tile swaps it for that item's facts/history/action.
 *
 * Photo thumbnails and "Open NCR" (codex review, closing the gap against the
 * approved design): the Plan envelope itself carries neither a photo
 * reference nor an `ncrNumber` for any row, so the PARENT (App.jsx) fetches
 * them on selection, via existing endpoints only (CisInspectionsQuery to
 * resolve `clientCaptureId` from the selected item's inspection number,
 * CisPhotosList + CisPhotoThumbnail for the thumbnails, CisNcrsQuery matched
 * by `sourceInspectionNumber` — see shape.js#findRegisterRowByInspectionNumber
 * / #findNcrForInspection). `extras` is that fetch's result, keyed by
 * nothing this component needs to check itself — the parent only ever hands
 * over extras for the item currently selected. `null`/`undefined` (nothing
 * to fetch, still loading, or fixture mode) renders neither section, never
 * an empty placeholder.
 */
// DEC-CHAT-20261007-412: the approved canvas's right-hand detail panel has
// no idle "nothing selected" state — `selected` is always real content now
// (Plan's own default-selection fallback picks the first backlog row or
// grid tile before anything is clicked), so this never needs to render a
// placeholder "Moving one" card.
const DETAIL_PANEL_STYLE = { flex: "0 0 320px", width: 320, boxSizing: "border-box" };

/**
 * A selected forecast item (the yard's forward schedule). Nothing here is a
 * request: the panel says so. "Book…" offers the inspector roster and confirms
 * through the same booking as dragging the tile onto a lane cell.
 */
function ForecastDetail({ item, lanes, onBookForecast, saving }) {
  const [booking, setBooking] = useState(false);
  const [inspector, setInspector] = useState("");
  const roster = rosterForProject(lanes, item.code);
  const confidence = FORECAST_LABEL[item.confidence] || "";
  const title = item.block && item.title ? `${item.label} ${item.title}` : item.label;
  const facts = [["Project", item.code]];
  if (item.side) facts.push(["Side", item.side === "P" ? "Port" : "Starboard"]);
  facts.push(["Forecast date", formatDay(item.date)]);
  if (item.division) facts.push(["Division", item.division]);
  facts.push(["Confidence", confidence]);
  return (
    <div className="card" data-testid="plan-detail-panel" data-detail-kind="forecast" style={DETAIL_PANEL_STYLE}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div className="t" style={{ fontSize: 15 }}>{title}</div>
        <div style={{ fontSize: 12, color: "var(--t3)" }}>{`${item.code} · ${confidence}`}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "88px minmax(0, 1fr)", gap: "4px 10px", fontSize: 12 }}>
        {facts.map(([k, v]) => (
          <React.Fragment key={k}>
            <span style={{ color: "var(--t3)" }}>{k}</span>
            <span style={{ color: "var(--t1)" }}>{v}</span>
          </React.Fragment>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ color: "var(--t3)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>History</span>
        <span data-testid="plan-forecast-detail-note" style={{ color: "var(--t2)", fontSize: 12 }}>Not yet requested by the yard</span>
      </div>
      {onBookForecast ? (
        booking ? (
          <div data-testid="plan-forecast-book" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--t3)" }}>
              Inspector
              <select data-testid="plan-forecast-book-inspector" value={inspector} onChange={(e) => setInspector(e.target.value)}>
                <option value="">Unassigned</option>
                {roster.map((r) => <option key={r.upn} value={r.upn}>{r.name}</option>)}
              </select>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn acc" data-testid="plan-forecast-book-confirm" disabled={saving} onClick={() => { setBooking(false); onBookForecast(item, { assignedTo: inspector, plannedDate: item.date }); }}>
                Book on {formatDay(item.date)}
              </button>
              <button type="button" className="btn off" onClick={() => setBooking(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button type="button" className="btn acc" data-testid="plan-forecast-book-open" disabled={saving} onClick={() => setBooking(true)}>Book…</button>
          </div>
        )
      ) : null}
    </div>
  );
}

function DetailPanel({ selected, bookHint, onPrimaryAction, onOpenRecord, extras, onOpenNcr, onRetryDetail, onReviewRecord, onBookForecast, savingRef = null, lanes = [] }) {
  const { runsById } = useContext(ImportProvenanceContext);
  // Owner retest 3: the column is ALWAYS rendered at a fixed width, so the
  // grid, week header and W-buttons never move between weeks.
  if (!selected) {
    return (
      <div className="card" data-testid="plan-detail-panel" style={DETAIL_PANEL_STYLE}>
        <div data-testid="plan-detail-empty" style={{ fontSize: 12, color: "var(--t3)" }}>
          Select an inspection to see its details
        </div>
      </div>
    );
  }
  if (selected.kind === "forecast") {
    return <ForecastDetail key={selected.key} item={selected.data} lanes={lanes} onBookForecast={onBookForecast} saving={Boolean(savingRef) && savingRef === selected.data.scheduleRef} />;
  }
  const detail = selected.kind === "tile" ? tileDetail(selected.data, runsById) : backlogDetail(selected.data, runsById);
  return (
    <div className="card" data-testid="plan-detail-panel" style={DETAIL_PANEL_STYLE}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div className="t" style={{ fontSize: 15 }}>
          {detail.title}
        </div>
        <div style={{ fontSize: 12, color: "var(--t3)" }}>{detail.status}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "88px minmax(0, 1fr)", gap: "4px 10px", fontSize: 12 }}>
        {detail.facts.map(([k, v]) => (
          <React.Fragment key={k}>
            <span style={{ color: "var(--t3)" }}>{k}</span>
            <span style={{ color: "var(--t1)" }}>{v}</span>
          </React.Fragment>
        ))}
      </div>
      {extras ? (
        <div data-testid="plan-detail-photos" style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {extras.loading ? (
            <span style={{ fontSize: 11, color: "var(--t3)" }}>Loading photos…</span>
          ) : extras.photosError ? (
            // Codex review round 2 (App.jsx:210): a FAILED read must never
            // render as if the project genuinely has no photos — that is
            // "No photos recorded" below, a different, true statement this
            // branch is never allowed to produce on a read failure.
            <>
              <span data-testid="plan-detail-photos-error" style={{ fontSize: 11, color: "var(--bad)" }}>Could not load photos</span>
              <button type="button" className="btn off" style={{ minHeight: 20, fontSize: 10, padding: "0 6px" }} onClick={onRetryDetail}>
                Retry
              </button>
            </>
          ) : extras.photos && extras.photos.length ? (
            extras.photos.map((p) => (
              <img
                key={p.key}
                src={p.url}
                alt={p.caption || "Inspection photo"}
                style={{ width: 72, height: 54, borderRadius: 4, objectFit: "cover", border: "1px solid var(--line)" }}
              />
            ))
          ) : (
            <span style={{ fontSize: 11, color: "var(--t3)" }}>No photos recorded</span>
          )}
        </div>
      ) : null}
      {extras && !extras.loading && extras.record ? <RecordReview view={extras.record} onReview={onReviewRecord} /> : null}
      {detail.history.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ color: "var(--t3)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>History</span>
          {detail.history.map((line, i) => (
            <span key={i} style={{ color: "var(--t2)", fontSize: 12 }}>
              {line}
            </span>
          ))}
        </div>
      ) : null}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
        {detail.primaryKind === "record" ? (
          onOpenRecord && detail.recordNumber ? (
            <button type="button" className="btn acc" data-testid="plan-detail-open-record" onClick={() => onOpenRecord({ code: selected.data.code, inspectionNumber: detail.recordNumber })}>
              {detail.primaryLabel}
            </button>
          ) : null
        ) : (
          <button type="button" className="btn acc" onClick={onPrimaryAction}>
            {detail.primaryLabel}
          </button>
        )}
        {extras && !extras.loading && extras.ncr ? (
          <button
            type="button"
            className="btn off"
            data-testid="plan-detail-open-ncr"
            onClick={() => onOpenNcr && onOpenNcr({ code: extras.ncr.code, ncrNumber: extras.ncr.ncrNumber })}
          >
            Open NCR
          </button>
        ) : null}
        {/* Codex review round 2 (App.jsx:210): a failed NCR read must say
            so — never render as "no NCR exists" (a true statement only
            once the read has actually succeeded and found none). */}
        {extras && !extras.loading && extras.ncrError ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span data-testid="plan-detail-ncr-error" style={{ fontSize: 11, color: "var(--bad)" }}>NCR status unknown</span>
            <button type="button" className="btn off" style={{ minHeight: 20, fontSize: 10, padding: "0 6px" }} onClick={onRetryDetail}>
              Retry
            </button>
          </span>
        ) : null}
      </div>
      {bookHint ? (
        <div data-testid="plan-detail-book-hint" style={{ fontSize: 11, color: "var(--t3)" }}>
          Drag this item onto a day or inspector in the grid to book it.
          <div data-testid="plan-detail-move-choices" style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {rosterForProject(lanes, selected.data.code).map((r) => (
              <span key={r.upn} className="chip mute" data-testid="plan-detail-move-choice">{r.name}</span>
            ))}
            <span className="chip mute" data-testid="plan-detail-move-choice">Unassigned</span>
          </div>
          <div style={{ marginTop: 4 }}>Only inspectors on the {selected.data.code} roster can take this item.</div>
        </div>
      ) : null}
    </div>
  );
}

const IMPORT_STATUS_STYLE = {
  new: { label: "new", color: "var(--acc)" },
  changed: { label: "changed", color: "var(--warn)" },
  unchanged: { label: "already imported", color: "var(--t3)" },
  reinstate: { label: "back on the yard's list", color: "var(--acc)" },
  retiring: { label: "removed from yard file", color: "var(--bad)" },
};

/** One selectable candidate row (owner row-level selection, 6 Oct 2026 feedback). */
function ImportCandidateRow({ row, checked, onToggle }) {
  const writable = row.status === "new" || row.status === "changed" || row.status === "reinstate" || row.kind === "cancel";
  const status = IMPORT_STATUS_STYLE[row.status] || IMPORT_STATUS_STYLE.unchanged;
  return (
    <div className="r" data-testid="plan-import-candidate" style={{ gap: 8, padding: "4px 4px" }}>
      <div
        role="checkbox"
        aria-checked={checked}
        aria-label={row.label}
        tabIndex={writable ? 0 : -1}
        data-testid="plan-import-candidate-checkbox"
        onClick={writable ? onToggle : undefined}
        onKeyDown={(e) => {
          if (!writable) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        style={{
          width: 14, height: 14, borderRadius: 3, flexShrink: 0,
          border: `1px solid ${checked ? "var(--acc)" : "var(--line2)"}`,
          background: checked ? "var(--acc)" : "transparent",
          cursor: writable ? "pointer" : "default",
          opacity: writable ? 1 : 0.4,
        }}
      />
      <span className="mono" style={{ fontSize: 11, color: "var(--t3)", width: 72, flexShrink: 0 }}>{row.date || "—"}</span>
      <span className="ttl" style={{ fontSize: 12, flexGrow: 1 }}>{row.label}</span>
      {row.type ? <span className="mono" style={{ fontSize: 11, color: "var(--t3)" }}>{row.type}</span> : null}
      <span className="chip" style={{ color: status.color, borderColor: status.color, width: "auto" }}>{status.label}</span>
    </div>
  );
}

/**
 * Renders the "Import yard schedule" control's current state, right of the
 * last-import line. Idle (`importPanel` null) renders nothing beyond the
 * button itself, so the artboard's screenshotted default state is
 * unchanged (AC-14) — the panel only appears once a project is picked and a
 * file is chosen. Every count shown is `shapeImportCounts`'s own labelled
 * rows from the server's response, never invented (SPEC-CIS-SITE-MANAGER-
 * COCKPIT-V3-001 §4.3).
 *
 * Owner row-level selection (6 Oct 2026 feedback — a 786-row preview with
 * one Apply committed the whole project): `importPanel.candidates` is the
 * bounded row list from `shapeImportCandidates`; each row carries its own
 * checkbox, default-checked for new/changed owner inspections
 * (`defaultImportSelection`, computed by App.jsx on preview). The "Owner
 * inspections only" toggle narrows what's SHOWN, never the selection
 * itself — unchecking a row and re-showing it (toggle off/on) keeps the
 * row unchecked. When the format carries no owner-attendance field (ITP
 * ledger — `ownerInspectionField` is null), the toggle is disabled with an
 * explanatory note rather than silently doing nothing.
 */
const COUNT_LABELS = {
  created: "created", updated: "updated", cancelled: "cancelled", duplicate: "duplicate",
  ignored_completed: "ignored (completed)", invalid: "invalid", error: "error", remaining: "remaining",
  stale: "stale", retired: "retired", vanishedCancelled: "vanished (cancelled)", reinstated: "reinstated (back on the yard's list)",
};

/** Persisted operation counters verbatim, under their real names (spec §4.5 — no derived row totals). */
export function formatImportCounts(counts) {
  const entries = Object.entries(counts || {});
  if (!entries.length) return "no counts recorded";
  return entries.map(([key, value]) => `${value} ${COUNT_LABELS[key] || key}`).join(" · ");
}

function ImportHistoryPanel({ history, onMore, onShowOnPlan, activeRunId }) {
  if (!history) return null;
  return (
    <div className="card" data-testid="plan-import-history" style={{ gap: 6 }}>
      <div className="t">Import history</div>
      {history.error ? (
        <div data-testid="plan-import-history-error" style={{ fontSize: 12, color: "var(--bad)" }}>Could not load import history ({history.error}).</div>
      ) : null}
      {!history.error && !history.loading && history.runs.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--t3)" }}>No yard schedule has been imported for this project.</div>
      ) : null}
      {history.runs.map((run) => (
        <div key={run.runId} data-testid="plan-import-history-row" style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "baseline", fontSize: 12 }}>
          <span className="mono" style={{ color: "var(--t3)" }}>{run.importedAt ? run.importedAt.slice(0, 16).replace("T", " ") : "—"}</span>
          <span>{run.fileName || run.runId}</span>
          <span style={{ color: "var(--t3)" }}>{run.importedBy}</span>
          <span className="mono" style={{ color: "var(--t2)" }}>{formatImportCounts(run.counts)}</span>
          {onShowOnPlan ? (
            <button
              type="button"
              className={`btn ${activeRunId === run.runId ? "acc" : "off"}`}
              data-testid="plan-import-history-show"
              style={{ minHeight: 22, fontSize: 11, padding: "0 8px" }}
              onClick={() => onShowOnPlan(run)}
            >
              Show on plan
            </button>
          ) : null}
        </div>
      ))}
      {history.loading ? <div style={{ fontSize: 12, color: "var(--t3)" }}>Loading…</div> : null}
      {history.hasMore && !history.loading ? (
        <button type="button" className="btn off" data-testid="plan-import-history-more" style={{ alignSelf: "flex-start" }} onClick={onMore}>Show older imports</button>
      ) : null}
    </div>
  );
}

/** "Applying… N of M" once the first pass has reported; plain "Applying…" before that. */
export function applyingLabel(progress) {
  if (progress && Number(progress.total) > 0) return `Applying… ${progress.done} of ${progress.total}`;
  return "Applying…";
}

export function ImportPanel({ importPanel, onCommit, onDismiss, onToggleRow, onToggleOwnerOnly, onSelectAllShown, onClearSelection }) {
  if (!importPanel) return null;
  const counts = importPanel.counts || [];
  const candidates = importPanel.candidates || [];
  const ownerOnly = Boolean(importPanel.ownerOnly);
  const filterAvailable = ownerFilterAvailable(candidates);
  const visibleRows = filterOwnerOnly(candidates, ownerOnly && filterAvailable);
  const selectedKeys = importPanel.selectedKeys || new Set();
  return (
    <div className="card" data-testid="plan-import-panel" style={{ marginTop: 8 }}>
      <div className="t">Yard schedule import</div>
      {importPanel.phase === "uploading" ? (
        <div style={{ fontSize: 12, color: "var(--t2)" }}>Uploading and reading…</div>
      ) : null}
      {importPanel.phase === "error" ? (
        <div data-testid="plan-import-error" style={{ fontSize: 12, color: "var(--bad)" }}>{importPanel.message}</div>
      ) : null}
      {importPanel.phase === "preview" || importPanel.phase === "committing" ? (
        <>
          <div data-testid="plan-import-counts" style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12, color: "var(--t2)" }}>
            {counts.map((c) => (
              <span key={c.label} className="mono">
                {c.label}: {c.count}
              </span>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "var(--t3)" }}>Nothing applied yet.</div>
          {candidates.length > 0 ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 8 }}>
                <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: filterAvailable ? "var(--t1)" : "var(--t3)" }}>
                  <input
                    type="checkbox"
                    data-testid="plan-import-owner-only"
                    checked={ownerOnly}
                    disabled={!filterAvailable || importPanel.phase === "committing"}
                    onChange={onToggleOwnerOnly}
                  />
                  Owner inspections only
                </label>
                <span
                  className="mono"
                  data-testid="plan-import-selected-count"
                  style={{ fontSize: 11, color: "var(--t3)" }}
                >
                  {selectedKeys.size} selected
                </span>
                <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
                  <button type="button" className="btn off" style={{ padding: "2px 8px", fontSize: 11 }} disabled={importPanel.phase === "committing"} onClick={onSelectAllShown}>
                    Select all shown
                  </button>
                  <button type="button" className="btn off" style={{ padding: "2px 8px", fontSize: 11 }} disabled={importPanel.phase === "committing"} onClick={onClearSelection}>
                    Clear
                  </button>
                </div>
              </div>
              {!filterAvailable ? (
                <div style={{ fontSize: 11, color: "var(--t3)" }}>
                  This file's format does not identify which inspections the owner attends — showing every row.
                </div>
              ) : null}
              {importPanel.candidatesTruncated ? (
                <div data-testid="plan-import-truncated" style={{ fontSize: 11, color: "var(--warn)" }}>
                  Showing {candidates.length} of {importPanel.candidatesTotal} rows — the rest are not listed and will not be
                  imported unless you narrow the file or run the import again.
                </div>
              ) : null}
              <div className="list" data-testid="plan-import-candidates" style={{ maxHeight: 260, overflowY: "auto", marginTop: 6 }}>
                {visibleRows.map((row) => (
                  <ImportCandidateRow
                    key={row.key}
                    row={row}
                    checked={selectedKeys.has(row.key)}
                    onToggle={() => onToggleRow(row.key)}
                  />
                ))}
              </div>
            </>
          ) : null}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" className="btn acc" disabled={importPanel.phase === "committing"} onClick={onCommit}>
              {importPanel.phase === "committing" ? applyingLabel(importPanel.progress) : "Apply"}
            </button>
            <button type="button" className="btn off" disabled={importPanel.phase === "committing"} onClick={onDismiss}>
              Cancel
            </button>
          </div>
        </>
      ) : null}
      {importPanel.phase === "committed" ? (
        <>
          <div data-testid="plan-import-counts" style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12, color: "var(--t2)" }}>
            {counts.map((c) => (
              <span key={c.label} className="mono">
                {c.label}: {c.count}
              </span>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "var(--ok)" }}>Applied. The schedule is live for this project.</div>
          <button type="button" className="btn off" style={{ marginTop: 4 }} onClick={onDismiss}>
            Close
          </button>
        </>
      ) : null}
    </div>
  );
}

/**
 * Plan screen — mockups/cockpit-v3/Plan.dc.html, element for element. `model`
 * is shape.js#shapePlanScreen's output (live) or the fixture equivalent —
 * both render through this one component, same discipline as Today.jsx.
 *
 * Drag and drop: dropping a tile posts ONE upsert changing only assignedTo
 * and/or plannedDate (`onDrop`); dropping the pinned overdue re-inspection
 * books a new row (`onDropPinned`). Neither ever moves the tile optimistically
 * — the parent re-fetches and this component always renders the server's
 * read-back position (SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001 §4.3).
 *
 * Yard schedule import (§4.3, DEC-CHAT-20260930-204): the button reuses the
 * existing preview/commit routes for exactly the one project `canImport`
 * names. With no single project selected ("All projects"), the click is
 * refused client-side via `onImportRefused` — never a file dialog, never a
 * request — and the parent renders that refusal through the SAME `notice`
 * banner drag-and-drop already uses. The file input stays hidden and out of
 * the artboard's idle screenshot; only `importPanel` (preview/committing/
 * committed/error) renders visible content, and only once a file has
 * actually been chosen.
 */
export default function Plan({
  model,
  pinnedBooking,
  notice,
  onDrop,
  onDropPinned,
  onPrevWeek,
  onNextWeek,
  weekLoading = null,
  savingRef = null,
  stale = false,
  onRetryStale,
  canImport = false,
  importPanel = null,
  onImportRefused,
  onImportFileChosen,
  onImportCommit,
  onImportDismiss,
  onImportToggleRow,
  onImportToggleOwnerOnly,
  onImportSelectAllShown,
  onImportClearSelection,
  // Import history (spec §4.5): `onLoadImportHistory(page)` resolves { ok, body: { runs, page, hasMore } }.
  canViewImportHistory = false,
  importHistoryScope = null,
  // Render-test seam only: start with the history list open.
  initialImportHistory = null,
  onLoadImportHistory,
  // Owner retest 4: recent import runs (runId, fileName, importedAt,
  // firstPlannedDate) the parent loaded, for "Imported from <file>" labels and
  // the "From import" filter; `onGoToWeek(mondayIso)` jumps the Plan to a week.
  importRuns = [],
  onGoToWeek,
  // Render-test / screenshot seam only: start with the import filter on.
  initialImportFilterRunId = null,
  // "To book" backlog paging (owner-approved redesign, DEC-CHAT-20261007-412
  // §4.3 — planView pages the backlog 20 at a time): `model.backlog` is
  // already the full accumulated list the parent has fetched so far;
  // `onShowMoreBacklog` fetches the next page.
  backlogLoadingMore = false,
  onShowMoreBacklog,
  // Explicit override (codex review finding, App.jsx:724 — a project
  // filter narrows `model.backlog`/`backlogTotal` to a scoped count where
  // `backlog.length < backlogTotal` is never a reliable "more exists"
  // signal; see shape.js#computeBacklogPaging). `null` (the default) keeps
  // the plain inference below, for fixture mode and any caller that has
  // not yet adopted scoped paging.
  canShowMoreBacklog = null,
  // Photo thumbnails + Open NCR (codex review gap-closure) — the parent
  // fetches these on selection (via `onDetailSelect`, below) since the Plan
  // envelope itself carries neither field; `detailExtras` is that fetch's
  // current result for whichever item is selected right now.
  onDetailSelect,
  detailExtras = null,
  onOpenNcr,
  onRetryDetail,
  // Day redesign phase 2: book a forecast item dropped on a lane cell
  // (`onBookForecast(item, { assignedTo, plannedDate })`), and approve / reopen /
  // undo the repair link of the selected record (`onReviewRecord(op, view, target)`).
  onBookForecast,
  onReviewRecord,
  // The full record, read only ("Open record"): `onLoadRecord(identity)` reads it,
  // `onLoadRecordPhoto(identity, photo)` one photo at full size, `onReleaseRecord()`
  // frees the photo URLs when the sheet closes. Without `onLoadRecord` there is no
  // record to open, so no "Open record" button is drawn.
  onLoadRecord,
  onLoadRecordPhoto,
  onReleaseRecord,
  // Render-test seam only: start with the repair-check list open.
  initialRepairOpen = false,
  initialSelectedRef = null,
  // "Open in Plan" (Report / Today): { code, scheduleRef, nonce }. Selects
  // that tile once the model for its week carries it; applied once per nonce.
  focusTarget = null,
  onFocusConsumed,
  // "+ Schedule" form: the selected projects (or all) and the save handler
  // (the existing schedule upsert). `initialScheduleOpen` is a render-test /
  // screenshot seam only — the form is NEVER open by default.
  scheduleProjects = [],
  onScheduleCreate,
  initialScheduleOpen = false,
  // Render-test seam only: start as if an item of this project is mid-drag.
  initialDragCode = null,
  // Render-test seam only: start with the "Move…" panel open.
  initialBookHint = false,
}) {
  const phone = usePhone();
  // Bumped each time an "Open in Plan" focus lands on a booking, so the phone
  // layout can open that booking's details (the desktop panel just selects it).
  const [focusNonce, setFocusNonce] = useState(0);
  const [scheduleOpen, setScheduleOpen] = useState(initialScheduleOpen);
  const scheduleButtonRef = useRef(null);
  const scheduleToday = (model.days.find((d) => d.isToday) || {}).date || new Date().toISOString().slice(0, 10);
  function closeSchedule() {
    setScheduleOpen(false);
    if (scheduleButtonRef.current) scheduleButtonRef.current.focus();
  }
  const [backlogFilter, setBacklogFilter] = useState("all");
  // `{ kind: "tile" | "backlog", key }` — a pure POINTER at whichever row/
  // tile the site manager clicked, never a data snapshot (codex review
  // finding: a snapshot taken at click time goes stale the moment `model`
  // refreshes, e.g. once that same row gets booked and moves to a grid
  // cell). `resolvePlanSelection` (shape.js) re-resolves this against the
  // CURRENT `model` on every render — see `selected` below.
  // `initialSelectedRef` (default null) exists only so a render test can
  // exercise the selected-detail-panel markup — same pattern as Report.jsx's
  // own `initialOpenLines` — since react-dom/server never fires the click
  // that would normally set this.
  const [selectedRef, setSelectedRef] = useState(initialSelectedRef);
  // Project code of the item currently being dragged (null when idle).
  const [dragCode, setDragCode] = useState(initialDragCode);
  const [bookHint, setBookHint] = useState(initialBookHint);
  const [recordOpen, setRecordOpen] = useState(null); // { code, inspectionNumber } while the record sheet is open
  const recordSheet = recordOpen && onLoadRecord ? (
    <RecordSheet
      key={`${recordOpen.code}:${recordOpen.inspectionNumber}`}
      identity={recordOpen}
      load={onLoadRecord}
      loadPhoto={onLoadRecordPhoto}
      onReview={onReviewRecord ? (op, view, target) => onReviewRecord(op, view, target, recordOpen) : undefined}
      onOpenNcr={onOpenNcr ? (payload) => { setRecordOpen(null); onOpenNcr(payload); } : undefined}
      onClose={() => setRecordOpen(null)}
      onRelease={onReleaseRecord}
      phone={phone}
    />
  ) : null;
  const openRecord = onLoadRecord ? (identity) => setRecordOpen(identity) : undefined;
  // One-shot: once applied, the parent clears the target so leaving Plan and
  // returning never re-selects it over a later choice.
  useEffect(() => {
    const ref = planFocusSelection(focusTarget, model);
    if (!ref) return;
    setSelectedRef(ref);
    setFocusNonce((n) => n + 1);
    if (onFocusConsumed) onFocusConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTarget, model]);
  const [history, setHistory] = useState(initialImportHistory); // null = closed
  const [importFilterRunId, setImportFilterRunId] = useState(initialImportFilterRunId);
  const [importFilterNote, setImportFilterNote] = useState(null);
  const runsById = useMemo(() => {
    const map = {};
    for (const run of [...importRuns, ...((history && history.runs) || [])]) {
      if (run && run.runId) map[run.runId] = { ...(map[run.runId] || {}), ...run };
    }
    return map;
  }, [importRuns, history]);
  const importContext = useMemo(() => ({ runsById, filterRunId: importFilterRunId }), [runsById, importFilterRunId]);
  function showRunOnPlan(run) {
    setImportFilterRunId(run.runId);
    const monday = run.firstPlannedDate ? mondayOfDate(run.firstPlannedDate) : null;
    if (monday && onGoToWeek) {
      setImportFilterNote(null);
      onGoToWeek(monday);
    } else {
      setImportFilterNote("This import has no dated rows recorded, so there is no week to jump to.");
    }
  }
  const fileInputRef = useRef(null);
  // Space starts/ends a keyboard drag, never Enter (codex review finding,
  // #1978 — the tile itself is both dnd-kit's keyboard-activation node AND
  // the selection control; Enter must mean only "select" there, never
  // double as a drag activator). Escape still cancels, matching dnd-kit's
  // own default.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: gridCoordinateGetter,
      keyboardCodes: { start: [KeyboardCode.Space], cancel: [KeyboardCode.Esc], end: [KeyboardCode.Space] },
    })
  );

  // Falls back to the default (first "To book" row, else first grid tile)
  // whenever nothing has been clicked yet — the approved canvas's detail
  // panel always shows real content (DEC-CHAT-20261007-412).
  const selected = useMemo(() => {
    if (selectedRef && selectedRef.kind === "forecast") {
      for (const cell of (model.forecast && model.forecast.cells) || []) {
        const item = cell.items.find((i) => i.key === selectedRef.key);
        if (item) return { kind: "forecast", key: item.key, data: item };
      }
      return defaultPlanSelection(model);
    }
    return resolvePlanSelection(selectedRef, model) || defaultPlanSelection(model);
  }, [selectedRef, model]);

  // The real-world inspection number behind the current selection, when one
  // exists — only a RECORDED inspection can ever have photos or an NCR. A
  // backlog "unbooked" row (not yet inspected) or a not-yet-done grid tile
  // resolves to `null`, and the parent fetches nothing for it.
  const detailInspectionNumber = !selected
    ? null
    : selected.kind === "tile"
      ? (selected.data.row && selected.data.row.recordedInspectionNumber) || null
      : selected.kind === "backlog" && selected.data.kind === "reinspection"
        ? selected.data.inspectionNumber
        : null;
  const detailCode = selected ? selected.data.code : null;

  useEffect(() => {
    if (onDetailSelect) onDetailSelect(detailCode && detailInspectionNumber ? { code: detailCode, inspectionNumber: detailInspectionNumber } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailCode, detailInspectionNumber]);

  async function loadHistoryPage(page, prior) {
    setHistory({ runs: prior, page, hasMore: false, loading: true, error: null });
    const result = await onLoadImportHistory(page);
    if (result && result.ok && result.body && Array.isArray(result.body.runs)) {
      setHistory({ runs: [...prior, ...result.body.runs], page: result.body.page || page, hasMore: Boolean(result.body.hasMore), loading: false, error: null });
    } else {
      setHistory({ runs: prior, page, hasMore: false, loading: false, error: (result && result.body && result.body.errorCode) || "unavailable" });
    }
  }

  // A different project selection invalidates an open history list.
  const firstScope = useRef(true);
  useEffect(() => {
    if (firstScope.current) { firstScope.current = false; return; }
    setHistory(null);
  }, [canViewImportHistory, importHistoryScope]);

  function toggleHistory() {
    if (history) { setHistory(null); return; }
    loadHistoryPage(1, []);
  }

  function selectBacklogRow(row) {
    if (focusTarget && onFocusConsumed) onFocusConsumed();
    setBookHint(false);
    setSelectedRef({ kind: "backlog", key: row.key });
  }

  function selectForecast(item) {
    if (focusTarget && onFocusConsumed) onFocusConsumed();
    setBookHint(false);
    setSelectedRef({ kind: "forecast", key: item.key });
  }

  function selectTile(tile) {
    if (focusTarget && onFocusConsumed) onFocusConsumed();
    setBookHint(false);
    setSelectedRef({ kind: "tile", key: planTileKey(tile) });
  }

  const weekNo = useMemo(() => isoWeek(model.weekStart), [model.weekStart]);
  // Week-switch speed (owner live-use defect): the parent only sets this to
  // a NETWORK-bound week (a cache miss) — a cached week renders instantly
  // and this stays null, exactly the "no network wait" target for a cache
  // hit. Non-null disables both week buttons so repeated clicks can never
  // skip weeks, and shows which week is loading rather than nothing.
  const loadingWeekNo = weekLoading ? isoWeek(weekLoading) : null;

  function handleImportButtonClick() {
    if (!canImport) {
      onImportRefused && onImportRefused();
      return;
    }
    fileInputRef.current && fileInputRef.current.click();
  }

  function handleFileInputChange(event) {
    const file = event.target.files && event.target.files[0];
    event.target.value = ""; // allow picking the same file again after a cancel/error
    if (file && onImportFileChosen) onImportFileChosen(file);
  }

  function handleDragStart(event) {
    const data = event.active && event.active.data.current;
    setDragCode((data && data.tile && data.tile.code) || null);
  }

  function handleDragEnd(event) {
    setDragCode(null);
    const { active, over } = event;
    if (!over) return;
    const activeData = active.data.current;
    const overData = over.data.current;
    if (!activeData || !overData) return;
    // Defence in depth: a lane off the item's project roster is never a drop.
    const targetLane = model.lanes.find((l) => l.key === (overData.upn || ""));
    if (targetLane && !laneAcceptsProject(targetLane, activeData.tile && activeData.tile.code)) return;
    if (activeData.forecast) {
      // Booking is a write: only a tile that is really over a lane cell books. The collision
      // detection always names the nearest cell, so a short drag would otherwise book it.
      const moved = active.rect && active.rect.current && active.rect.current.translated;
      const cell = over.rect;
      const inside = moved && cell && moved.left + moved.width / 2 >= cell.left && moved.left + moved.width / 2 <= cell.left + cell.width && moved.top + moved.height / 2 >= cell.top && moved.top + moved.height / 2 <= cell.top + cell.height;
      if (inside && onBookForecast) onBookForecast(activeData.forecast, { assignedTo: overData.upn, plannedDate: overData.date });
    } else if (activeData.pinned) {
      onDropPinned(activeData.tile, { assignedTo: overData.upn, plannedDate: overData.date });
    } else {
      onDrop(activeData.tile, { assignedTo: overData.upn, plannedDate: overData.date });
    }
  }

  if (phone) {
    return (
      <>
      <PhonePlan
        ctx={{
          model, weekNo, loadingWeekNo, stale, weekLoading, onPrevWeek, onNextWeek, onRetryStale, notice, savingRef, pinnedBooking,
          onDrop, onDropPinned, selected, selectTile, tileDetail, runsById, focusNonce,
          detailExtras: detailInspectionNumber ? detailExtras : null, onRetryDetail, onOpenNcr, onReviewRecord, onOpenRecord: openRecord, onBookForecast, initialRepairOpen,
          backlogFilter, setBacklogFilter,
          canShowMoreBacklog: canShowMoreBacklog !== null ? canShowMoreBacklog : Boolean(onShowMoreBacklog) && model.backlog.length < model.backlogTotal,
          showMoreCount: Math.max(0, Math.min(model.backlogLimit, model.backlogTotal - model.backlog.length)),
          onShowMoreBacklog, backlogLoadingMore,
          fileInputRef, onFileInputChange: handleFileInputChange, onImportButtonClick: handleImportButtonClick,
          historyEnabled: Boolean(canViewImportHistory && onLoadImportHistory), onToggleHistory: toggleHistory, history,
          onMoreHistory: () => history && loadHistoryPage(history.page + 1, history.runs), showRunOnPlan, formatImportCounts,
          loadHistoryIfClosed: () => { if (!history && canViewImportHistory && onLoadImportHistory) loadHistoryPage(1, []); },
          importPanel, importCode: (importPanel && importPanel.code) || importHistoryScope, importHistoryScope,
          onImportDismiss, onImportCommit, onImportToggleOwnerOnly, onImportSelectAllShown, onImportClearSelection, onImportToggleRow,
          importStatusLabel: (status) => (IMPORT_STATUS_STYLE[status] || IMPORT_STATUS_STYLE.unchanged).label, applyingLabel,
          ScheduleForm, scheduleProjects, scheduleToday, onScheduleCreate,
        }}
      />
      {recordSheet}
      </>
    );
  }

  const toBookColumn = (
    <ToBookColumn
      backlog={model.backlog}
      backlogTotal={model.backlogTotal}
      filter={backlogFilter}
      onFilterChange={setBacklogFilter}
      canShowMore={canShowMoreBacklog !== null ? canShowMoreBacklog : Boolean(onShowMoreBacklog) && model.backlog.length < model.backlogTotal}
      showMoreCount={Math.max(0, Math.min(model.backlogLimit, model.backlogTotal - model.backlog.length))}
      onShowMore={onShowMoreBacklog}
      loadingMore={backlogLoadingMore}
      selectedKey={selected && selected.kind === "backlog" ? selected.key : null}
      onSelect={selectBacklogRow}
      savingRef={savingRef}
      pinnedBooking={pinnedBooking}
    />
  );

  return (
    <ImportProvenanceContext.Provider value={importContext}>
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setDragCode(null)}>
      {/*
       * Three columns at desktop width (owner-approved redesign, DEC-CHAT-
       * 20261007-412): left = "To book"; centre = the yard schedule panel,
       * week header, legend and grid; right = the detail panel. `flexWrap`
       * stacks them (To book, then centre, then detail) at narrow width,
       * the same order the approved canvas stacks in.
       */}
      <div className="page" style={{ flexWrap: "wrap", alignItems: "flex-start", gap: 20 }}>
        {model.repairChecks.count > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1, minWidth: 280, maxWidth: 400 }}>
            <RepairChecksCard repairChecks={model.repairChecks} initialOpen={initialRepairOpen} />
            {toBookColumn}
          </div>
        ) : (
          toBookColumn
        )}

        <div style={{ flex: "999 1 560px", minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
          <YardSchedulePanel
            lastImport={model.lastImport}
            fileInputRef={fileInputRef}
            onImportButtonClick={handleImportButtonClick}
            onFileInputChange={handleFileInputChange}
            historyEnabled={Boolean(canViewImportHistory && onLoadImportHistory)}
            historyOpen={Boolean(history)}
            onToggleHistory={toggleHistory}
          />
          <ImportHistoryPanel history={history} onMore={() => loadHistoryPage(history.page + 1, history.runs)} onShowOnPlan={showRunOnPlan} activeRunId={importFilterRunId} />
          <ImportPanel
            importPanel={importPanel}
            onCommit={onImportCommit}
            onDismiss={onImportDismiss}
            onToggleRow={onImportToggleRow}
            onToggleOwnerOnly={onImportToggleOwnerOnly}
            onSelectAllShown={onImportSelectAllShown}
            onClearSelection={onImportClearSelection}
          />

          <div className="plan-week-head">
            <div className="h1">Week {weekNo}</div>
            <div className="lead">{model.lead}</div>
            {loadingWeekNo ? (
              <div className="chip mute" data-testid="plan-week-loading" style={{ flexShrink: 0 }}>
                Loading week {loadingWeekNo}…
              </div>
            ) : null}
            {/* A cache hit rendered instantly but its background revalidation
                failed (codex review finding, PR #1957, HIGH) — the data on
                screen is the last one actually confirmed, just not
                reconfirmed just now; never silently treated as current. */}
            {stale && !loadingWeekNo ? (
              <button type="button" className="chip warn" data-testid="plan-week-stale" onClick={onRetryStale} style={{ border: "none", cursor: "pointer", flexShrink: 0 }}>
                Not up to date — Retry
              </button>
            ) : null}
            <div className="plan-week-nav">
              <button type="button" className="btn off" disabled={Boolean(weekLoading)} onClick={onPrevWeek}>
                W{weekNo - 1}
              </button>
              <button type="button" className="btn off" disabled={Boolean(weekLoading)} onClick={onNextWeek}>
                W{weekNo + 1}
              </button>
              <button
                type="button"
                className="btn"
                ref={scheduleButtonRef}
                data-testid="plan-schedule-button"
                aria-expanded={scheduleOpen}
                aria-controls="plan-schedule-form"
                disabled={!onScheduleCreate}
                onClick={() => (scheduleOpen ? closeSchedule() : setScheduleOpen(true))}
              >
                + Schedule
              </button>
            </div>
          </div>

          {scheduleOpen && onScheduleCreate ? (
            <ScheduleForm
              projects={scheduleProjects}
              lanes={model.lanes}
              today={scheduleToday}
              onCreate={onScheduleCreate}
              onClose={closeSchedule}
            />
          ) : null}

          {model.errors.length > 0 ? (
            <div className="list" data-testid="plan-errors">
              {model.errors.map((e) => (
                <div className="r" key={e.code}>
                  <div className="chip bad age">!</div>
                  <div className="proj">{e.code}</div>
                  <div className="ttl" style={{ color: "var(--bad)" }}>
                    Unavailable &mdash; {e.error}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {notice ? (
            <div className="r" data-testid="plan-notice" style={{ minHeight: 36, border: "1px solid var(--warn-br)", borderRadius: 8, background: "var(--warn-bg)" }}>
              <div style={{ color: "var(--warn)", fontSize: 12 }}>{notice}</div>
            </div>
          ) : null}

          <LegendRow />
          {model.forecast.hasAny ? <ForecastLegend /> : null}
          <ImportFilterBar
            model={model}
            runsById={runsById}
            filterRunId={importFilterRunId}
            filterNote={importFilterNote}
            onToggle={(id) => { setImportFilterNote(null); setImportFilterRunId(id); }}
            onClear={() => { setImportFilterNote(null); setImportFilterRunId(null); }}
          />

          {/*
           * Horizontal scroll, never a squeeze (owner review 2026-10,
           * round 2): the day grid's own column template (`.list` below,
           * `GRID_TEMPLATE_COLUMNS`) never shrinks a day column below
           * 130px — on a narrower viewport (including phone) the grid runs
           * wider than this box and scrolls sideways to reveal it, the
           * same way the canvas's own grid does, rather than crushing
           * every tile down to illegible text.
           */}
          <div className="list" style={{ overflowX: "auto" }}>
            <div style={{ minWidth: GRID_MIN_WIDTH }}>
              <div style={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE_COLUMNS, background: "var(--surface)" }}>
                <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--t3)" }}>Inspector &middot; site team</div>
                {model.days.map((day) => (
                  <div
                    key={day.date}
                    style={{
                      padding: "8px 12px",
                      borderLeft: "1px solid var(--line)",
                      fontSize: 12,
                      fontWeight: 600,
                      color: day.isToday ? "var(--acc)" : "var(--t2)",
                    }}
                  >
                    <span>{day.label}</span> <span className="mono" style={{ fontSize: 11, fontWeight: 500, color: "var(--t3)" }}>{day.dayMonth}</span>
                  </div>
                ))}
              </div>
              {model.lanes.map((lane) => (
                <LaneRow
                  key={lane.key}
                  lane={lane}
                  days={model.days}
                  savingRef={savingRef}
                  selectedKey={selected && selected.kind === "tile" ? selected.key : null}
                  onSelectTile={selectTile}
                  dragCode={dragCode}
                />
              ))}
              {model.forecast.hasAny ? <ForecastRow forecast={model.forecast} savingRef={savingRef} selectedKey={selected && selected.kind === "forecast" ? selected.key : null} onSelectForecast={selectForecast} /> : null}
            </div>
          </div>
          <p style={{ margin: 0, color: "var(--t3)", fontSize: 12 }}>
            Inspections recorded in the app appear on their day as <span className="chip ok">done</span>, also when nobody booked them.
          </p>
          <LaterWeeks weeks={model.forecast.laterWeeks} />
        </div>

        <DetailPanel
          selected={selected}
          bookHint={bookHint}
          onPrimaryAction={() => setBookHint(true)}
          onOpenRecord={openRecord}
          extras={detailInspectionNumber ? detailExtras : null}
          onOpenNcr={onOpenNcr}
          onRetryDetail={onRetryDetail}
          onReviewRecord={onReviewRecord}
          onBookForecast={onBookForecast}
          savingRef={savingRef}
          lanes={model.lanes}
        />
      </div>
    </DndContext>
    {recordSheet}
    </ImportProvenanceContext.Provider>
  );
}

function isoWeek(dateStr) {
  const parsed = Date.parse(`${dateStr}T00:00:00Z`);
  if (!Number.isFinite(parsed)) return 0;
  const target = new Date(parsed);
  target.setUTCDate(target.getUTCDate() + 4 - (target.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
}

/**
 * In-page "+ Schedule" form (Plan). Saves through the parent's `onCreate`
 * (the existing schedule upsert); validation mirrors the server's
 * validateScheduleItem, so a form that passes here is a body the server
 * accepts. Errors show inline, next to the field or at the form foot (server
 * refusal). Fully keyboard operable: fields are native controls in tab order,
 * Enter submits, Escape closes. No window.prompt / confirm / alert.
 */
function ScheduleForm({ projects, lanes, today, onCreate, onClose }) {
  const projectList = projects && projects.length ? projects : [];
  const [values, setValues] = useState(() => defaultScheduleForm({ code: projectList.length === 1 ? projectList[0] : "", today }));
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [saving, setSaving] = useState(false);
  const firstRef = useRef(null);

  useEffect(() => {
    if (firstRef.current) firstRef.current.focus();
  }, []);

  const roster = useMemo(() => rosterForProject(lanes, values.code), [lanes, values.code]);

  function set(field, value) {
    setValues((v) => {
      const next = { ...v, [field]: value };
      // An inspector chosen for one project is not carried to another.
      if (field === "code") next.assignedTo = "";
      return next;
    });
    setErrors((e) => ({ ...e, [field]: undefined }));
    setServerError(null);
  }

  async function submit(event) {
    event.preventDefault();
    if (saving) return;
    const checked = validateScheduleForm(values, { projectCodes: projectList, roster });
    if (!checked.ok) {
      setErrors(checked.errors);
      const firstBad = Object.keys(checked.errors)[0];
      const node = document.getElementById(`sf-${firstBad}`);
      if (node) node.focus();
      return;
    }
    setSaving(true);
    setServerError(null);
    try {
      const result = await onCreate(values.code, checked.item);
      if (result && result.ok) {
        onClose();
        return;
      }
      setServerError((result && result.message) || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  function onKeyDown(event) {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
    }
  }

  function field(id, label, control, error, hint) {
    return (
      <div className="sf-field">
        <label htmlFor={`sf-${id}`}>{label}</label>
        {control}
        {error ? (
          <div className="sf-err" id={`sf-${id}-err`} role="alert">
            {error}
          </div>
        ) : hint ? (
          <div className="sf-hint">{hint}</div>
        ) : null}
      </div>
    );
  }

  const bad = (f) => (errors[f] ? { "aria-invalid": "true", "aria-describedby": `sf-${f}-err` } : {});

  return (
    <form
      id="plan-schedule-form"
      className="card sf"
      data-testid="plan-schedule-form"
      aria-label="Schedule an inspection"
      onSubmit={submit}
      onKeyDown={onKeyDown}
      noValidate
    >
      <div className="t">Schedule an inspection</div>
      <div className="sf-grid">
        {field(
          "code",
          "Project",
          <select id="sf-code" ref={firstRef} value={values.code} disabled={saving} onChange={(e) => set("code", e.target.value)} {...bad("code")}>
            <option value="">Choose project…</option>
            {projectList.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>,
          errors.code
        )}
        {field(
          "title",
          "Item",
          <input id="sf-title" type="text" maxLength={255} value={values.title} disabled={saving} onChange={(e) => set("title", e.target.value)} {...bad("title")} />,
          errors.title
        )}
        {field(
          "category",
          "Trade",
          <select id="sf-category" value={values.category} disabled={saving} onChange={(e) => set("category", e.target.value)} {...bad("category")}>
            <option value="">Choose trade…</option>
            {SCHEDULE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>,
          errors.category
        )}
        {field(
          "stage",
          "Stage",
          <input id="sf-stage" type="text" maxLength={255} value={values.stage} disabled={saving} onChange={(e) => set("stage", e.target.value)} {...bad("stage")} />,
          errors.stage
        )}
        {field(
          "inspectionType",
          "Type",
          <select id="sf-inspectionType" value={values.inspectionType} disabled={saving} onChange={(e) => set("inspectionType", e.target.value)} {...bad("inspectionType")}>
            {SCHEDULE_INSPECTION_TYPES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>,
          errors.inspectionType
        )}
        {field(
          "plannedDate",
          "Day",
          <input id="sf-plannedDate" type="date" value={values.plannedDate} disabled={saving} onChange={(e) => set("plannedDate", e.target.value)} {...bad("plannedDate")} />,
          errors.plannedDate
        )}
        {field(
          "plannedTime",
          "Time (optional)",
          <input id="sf-plannedTime" type="time" value={values.plannedTime} disabled={saving} onChange={(e) => set("plannedTime", e.target.value)} {...bad("plannedTime")} />,
          errors.plannedTime
        )}
        {field(
          "assignedTo",
          "Inspector",
          <select id="sf-assignedTo" value={values.assignedTo} disabled={saving} onChange={(e) => set("assignedTo", e.target.value)} {...bad("assignedTo")}>
            <option value="">Unassigned</option>
            {roster.map((r) => (
              <option key={r.upn} value={r.upn}>
                {r.name}
              </option>
            ))}
          </select>,
          errors.assignedTo,
          values.code ? null : "Choose a project first"
        )}
      </div>
      {serverError ? (
        <div className="sf-err" role="alert" data-testid="plan-schedule-error">
          Could not schedule: {serverError}
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="btn" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="btn acc" disabled={saving} data-testid="plan-schedule-save">
          {saving ? "Saving…" : "Schedule"}
        </button>
      </div>
    </form>
  );
}
